import React, { Suspense, lazy, useEffect } from "react";
import { 
  Navigate, 
  useLocation, 
  Outlet,
  createBrowserRouter,
  RouterProvider,
  ScrollRestoration
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";
import { initializeAuth, selectUser, selectIsAuthenticated, selectIsLoading, selectUserRole } from "./Apis/store/authSlice";
import { SWRProvider } from "./Apis/swrConfig";
import { ReduxProvider } from "./Apis/store/ReduxProvider";
import { ToastProvider } from "./Components/Toast";
import LoginGuard from "./Componats/Login/LoginGuard";
import Navbar from "./Componats/Nav/Nav";
import Footer from "./Componats/Footer/Footer";

// Lazy load components for better performance
const Home = lazy(() => import("./Componats/Layout/Home"));
// الصفحات العامة. كانت القائمة تشير إلى مراسٍ داخل الصفحة الرئيسية
// (‎#academy و‎#services و‎#features)، فلم تكن هناك عناوين قابلة للمشاركة
// ولا عنوان مستقل لكل صفحة. راجع content/navigation.js.
const AboutPage = lazy(() => import("./Componats/Pages/AboutPage"));
const ProgramsPage = lazy(() => import("./Componats/Pages/ProgramsPage"));
const AccreditationPage = lazy(() => import("./Componats/Pages/AccreditationPage"));
const ContactPage = lazy(() => import("./Componats/Pages/ContactPage"));
const Dashboard = lazy(() => import("./Dashboard/Dashboard"));
const Login = lazy(() => import("./Componats/Login/Login"));
const Singup = lazy(() => import("./Componats/Login/SingUp"));
const PrivacyPolicy = lazy(() => import("./Componats/PrivacyPolicy/PrivacyPolicy"));
const PrivacyPolicy2 = lazy(() => import("./Componats/PrivacyPolicy/PrivacyPolicy2"));
const NotFound = lazy(() => import("./Componats/404/NotFound"));

// Admin Components
const StudentTable = lazy(() => import("./Dashboard/Admin/StudentTable"));
const GeneralLooka = lazy(() => import("./Dashboard/Admin/GeneralLooka"));
const StudentProfileA = lazy(() => import("./Dashboard/Admin/StudentProfilea"));
const LecturerTable = lazy(() => import("./Dashboard/Admin/Luctuera"));
const GroupsPage = lazy(() => import("./Dashboard/Admin/Groups"));
const Studyyears = lazy(() => import("./Dashboard/Admin/Studyyears"));
const SubjectAdmin = lazy(() => import("./Dashboard/Admin/Subjectsa"));
const FollowUp = lazy(() => import("./Dashboard/Admin/Followup"));

const QuestionManagement = lazy(() => import("./Dashboard/Admin/QuestionManagement"));

const ExamList = lazy(() => import("./Dashboard/Admin/ExamList"));
const Result = lazy(() => import("./Dashboard/Admin/Result"));

// Student Components
const QuizeUser = lazy(() => import("./Dashboard/QuizeUser/QuizeUser"));
const StudentProfile = lazy(() => import("./Dashboard/StudentTable/Profilest"));
const GeneralLook = lazy(() => import("./Dashboard/StudentTable/GeneralLook"));
// const PastExams = lazy(() => import("./Dashboard/StudentTable/PastExams"));


// Loading component for Suspense
const LoadingSpinner = () => (
  <div className="min-h-screen bg-[#2E3E58] flex items-center justify-center">
    <div className="text-white text-xl">جاري التحميل...</div>
  </div>
);

// Route configuration for better organization
const ROUTES = {
  PUBLIC: {
    HOME: "/",
    LOGIN: "/login",
    SIGNUP: "/SingUP",
    PRIVACY: "/privacy-policy",
    TERMS: "/terms-and-conditions",
  },
  DASHBOARD: {
    BASE: "/dashboard",
    ADMIN: {
      GENERAL: "/dashboard/GeneralLooka",
      STUDENTS: "/dashboard/studetsAdmin",
      LECTURERS: "/dashboard/LecturerTable",
      EXAMS: "/dashboard/ExamList",
      RESULTS: "/dashboard/Result",
      QUESTIONS: "/dashboard/QuestionManagement",
      GROUPS: "/dashboard/GroupsPage",
      YEARS: "/dashboard/Studyyears",
      SUBJECTS: "/dashboard/SubjectAdmin",
      FOLLOWUP: "/dashboard/FollowUp",
    },
    STUDENT: {
      GENERAL: "/dashboard/GeneralLooks",
      PROFILE: "/dashboard/StudentProfile",
      QUIZ: "/dashboard/QuizeUser",
      // PAST_EXAMS: "/dashboard/PastExams",
    },
  },
};

// Route guard components that will be used inside the provider
const ProtectedRoute = ({ children, requiredRoles = [], fallback = null }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const isLoading = useSelector(selectIsLoading);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to={ROUTES.PUBLIC.LOGIN} replace />;
  }

  if (requiredRoles.length && !requiredRoles.includes(user.role)) {
    const redirectPath = user.role === "أدمن" 
      ? ROUTES.DASHBOARD.ADMIN.GENERAL 
      : ROUTES.DASHBOARD.STUDENT.GENERAL;
    return <Navigate to={redirectPath} replace />;
  }

  return children || fallback;
};

const AdminRoute = ({ children }) => (
  <ProtectedRoute requiredRoles={["أدمن"]}>
    {children}
  </ProtectedRoute>
);

const StudentRoute = ({ children }) => (
  <ProtectedRoute requiredRoles={["طالب"]}>
    {children}
  </ProtectedRoute>
);

const AdminOrStudentRoute = ({ children }) => (
  <ProtectedRoute requiredRoles={["طالب", "أدمن"]}>
    {children}
  </ProtectedRoute>
);

// Create router configuration function that can be called inside UserProvider
const createAppRouter = () => createBrowserRouter([
  {
    path: "/",
    element: <AppContent />,
    children: [
      // Public Routes
      {
        index: true,
        element: <Home />
      },
      // الصفحات العامة — تُبنى من نفس المصدر الذي تُبنى منه القائمة،
      // فلا يمكن أن يشير رابط في القائمة إلى مسار غير موجود.
      { path: "about",         element: <AboutPage /> },
      { path: "programs",      element: <ProgramsPage /> },
      { path: "accreditation", element: <AccreditationPage /> },
      { path: "contact",       element: <ContactPage /> },
      {
        path: "login",
        element: (
          <LoginGuard>
            <Login />
          </LoginGuard>
        )
      },
      {
        path: "SingUP",
        element: <Singup />
      },
      {
        path: "privacy-policy",
        element: <PrivacyPolicy />
      },
      {
        path: "terms-and-conditions",
        element: <PrivacyPolicy2 />
      },
      // 404 Catch-all route - must be last
      {
        path: "*",
        element: <NotFound />
      },
      // Dashboard Routes with protection
      {
        path: "dashboard",
        element: <AdminOrStudentRoute><Dashboard /></AdminOrStudentRoute>,
        children: [
          // Admin Routes
          {
            path: "studetsAdmin",
            element: <AdminRoute><StudentTable /></AdminRoute>
          },
          {
            path: "ExamList",
            element: <AdminRoute><ExamList /></AdminRoute>
          },
          {
            path: "Result",
            element: <AdminRoute><Result /></AdminRoute>
          },
        
        
          {
            path: "FollowUp",
            element: <AdminRoute><FollowUp /></AdminRoute>
          },
          {
            path: "QuestionManagement",
            element: <AdminRoute><QuestionManagement /></AdminRoute>
          },
          {
            path: "GroupsPage",
            element: <AdminRoute><GroupsPage /></AdminRoute>
          },
          {
            path: "Studyyears",
            element: <AdminRoute><Studyyears /></AdminRoute>
          },
          {
            path: "SubjectAdmin",
            element: <AdminRoute><SubjectAdmin /></AdminRoute>
          },
          {
            path: "GeneralLooka",
            element: <AdminRoute><GeneralLooka /></AdminRoute>
          },
          {
            path: "StudentProfileA/:id",
            element: <AdminRoute><StudentProfileA /></AdminRoute>
          },
          {
            path: "LecturerTable",
            element: <AdminRoute><LecturerTable /></AdminRoute>
          },
          // Student Routes
          {
            path: "QuizeUser",
            element: <StudentRoute><QuizeUser /></StudentRoute>
          },
          {
            path: "StudentProfile",
            element: <AdminOrStudentRoute><StudentProfile /></AdminOrStudentRoute>
          },
          // {
          //   path: "PastExams",
          //   element: <StudentRoute><PastExams /></StudentRoute>
          // },
          {
            path: "GeneralLooks",
            element: <StudentRoute><GeneralLook /></StudentRoute>
          }
        ]
      }
    ]
  }
], {
  /*
   | Vite substitutes BASE_URL with whatever --base the build used: "/" for a
   | normal build, "/preview/" for the side-by-side build that gets deployed
   | next to the live bundle for comparison.
   |
   | Without this the router would match paths against "/" while the app is
   | actually served from "/preview/", so every route resolves to the 404 page.
   | For the normal build the value is "/" and nothing changes.
   */
  basename: import.meta.env.BASE_URL.replace(/\/$/, '') || '/',
});

function Main() {
  return (
    <ReduxProvider>
      <SWRProvider>
        <ToastProvider>
          <RouterProvider router={createAppRouter()} />
        </ToastProvider>
      </SWRProvider>
    </ReduxProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const dispatch = useDispatch();

  // Initialize auth state from cookies on app load
  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  const hideHeaderRoutes = ["/dashboard", "/privacy-policy" , "/terms-and-conditions"];
  const shouldHideHeader = hideHeaderRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  return (
    <div className="relative">
      {/*
       | رابط التخطّي إلى المحتوى. من يتنقّل بلوحة المفاتيح كان مضطرًا إلى
       | المرور على روابط القائمة كلها في كل صفحة قبل الوصول إلى محتواها.
       | الرابط مخفي بصريًا ولا يظهر إلا عند وصول التركيز إليه.
       */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:start-3 focus:z-[60] focus:rounded-lg focus:bg-accent-600 focus:px-5 focus:py-3 focus:font-bold focus:text-white"
      >
        تخطَّ إلى المحتوى
      </a>

      {!shouldHideHeader && <Navbar />}

      <Suspense fallback={<LoadingSpinner />}>
        <Outlet />
      </Suspense>
      
      {!shouldHideHeader && <Footer />}
      
      {/* Scroll Restoration */}
      <ScrollRestoration />
      
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={true}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastClassName="text-end"
      />
    </div>
  );
}

export default Main;
